/**
 * Folder rail. Folders are purely organisational on the server — deleting one
 * unfiles its images rather than deleting them — and the copy here says so,
 * because "delete folder" reads as "delete the pictures in it" otherwise.
 */

import { useMemo, useState } from 'react';
import {
  ChevronRightIcon,
  FolderIcon,
  FolderOpenIcon,
  PencilIcon,
  PlusIcon,
  Squares2X2Icon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { Button, Input, Modal, ModalFooter } from '@/components/common';
import { cn } from '@/utils';
import type { MediaFolder } from '@/services/assets';
import {
  useCreateAssetFolder,
  useDeleteAssetFolder,
  useUpdateAssetFolder,
} from '@/hooks/useAssets';

/** `null` = every asset, `'none'` = the ones with no folder. */
export type FolderSelection = string | null | 'none';

interface FolderNavProps {
  folders: MediaFolder[];
  selected: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  totalCount?: number;
  isLoading?: boolean;
  /** Hide the create/rename/delete controls (the picker is read-only). */
  readOnly?: boolean;
  className?: string;
}

interface FolderNode extends MediaFolder {
  children: FolderNode[];
}

/** Flat list → tree. Orphans (parent outside the visible scope) sit at root. */
function buildTree(folders: MediaFolder[]): FolderNode[] {
  const byId = new Map<string, FolderNode>();
  folders.forEach((folder) => byId.set(folder.id, { ...folder, children: [] }));
  const roots: FolderNode[] = [];
  byId.forEach((node) => {
    const parent = node.parent ? byId.get(node.parent) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  return roots;
}

export function FolderNav({
  folders,
  selected,
  onSelect,
  totalCount,
  isLoading,
  readOnly = false,
  className,
}: FolderNavProps) {
  const [creatingUnder, setCreatingUnder] = useState<string | null | undefined>(undefined);
  const [renaming, setRenaming] = useState<MediaFolder | null>(null);
  const [deleting, setDeleting] = useState<MediaFolder | null>(null);
  const [name, setName] = useState('');

  const createFolder = useCreateAssetFolder();
  const updateFolder = useUpdateAssetFolder();
  const deleteFolder = useDeleteAssetFolder();

  const tree = useMemo(() => buildTree(folders), [folders]);
  const unfiledLabel = 'Not in a folder';

  return (
    <div className={cn('space-y-1', className)}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Folders
        </p>
        {!readOnly && (
          <button
            type="button"
            onClick={() => {
              setCreatingUnder(null);
              setName('');
            }}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"
            title="New folder"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <FolderRow
        icon={<Squares2X2Icon className="h-4 w-4" />}
        label="All images"
        count={totalCount}
        active={selected === null}
        onClick={() => onSelect(null)}
      />
      <FolderRow
        icon={<FolderIcon className="h-4 w-4" />}
        label={unfiledLabel}
        active={selected === 'none'}
        onClick={() => onSelect('none')}
      />

      {isLoading ? (
        <div className="space-y-1 pt-2">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-8 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700" />
          ))}
        </div>
      ) : (
        <div className="pt-1">
          {tree.map((node) => (
            <FolderBranch
              key={node.id}
              node={node}
              depth={0}
              selected={selected}
              onSelect={onSelect}
              readOnly={readOnly}
              onAddChild={(parentId) => {
                setCreatingUnder(parentId);
                setName('');
              }}
              onRename={(folder) => {
                setRenaming(folder);
                setName(folder.name);
              }}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}

      {/* --- create --- */}
      <Modal
        isOpen={creatingUnder !== undefined}
        onClose={() => setCreatingUnder(undefined)}
        title="New folder"
        size="sm"
      >
        <Input
          label="Folder name"
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          placeholder="Brand assets"
        />
        <ModalFooter>
          <Button variant="outline" onClick={() => setCreatingUnder(undefined)}>
            Cancel
          </Button>
          <Button
            isLoading={createFolder.isPending}
            disabled={!name.trim()}
            onClick={() =>
              createFolder.mutate(
                { name: name.trim(), parent: creatingUnder ?? null },
                { onSuccess: () => setCreatingUnder(undefined) },
              )
            }
          >
            Create
          </Button>
        </ModalFooter>
      </Modal>

      {/* --- rename --- */}
      <Modal isOpen={Boolean(renaming)} onClose={() => setRenaming(null)} title="Rename folder" size="sm">
        <Input
          label="Folder name"
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
        />
        <ModalFooter>
          <Button variant="outline" onClick={() => setRenaming(null)}>
            Cancel
          </Button>
          <Button
            isLoading={updateFolder.isPending}
            disabled={!name.trim()}
            onClick={() =>
              renaming &&
              updateFolder.mutate(
                { id: renaming.id, data: { name: name.trim() } },
                { onSuccess: () => setRenaming(null) },
              )
            }
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* --- delete --- */}
      <Modal
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={`Delete "${deleting?.name || ''}"?`}
        size="sm"
      >
        <p className="text-sm text-gray-600">
          The {deleting?.asset_count || 0} image{deleting?.asset_count === 1 ? '' : 's'} inside will be
          kept and moved to <strong>{unfiledLabel}</strong>. Nothing on your website changes.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={deleteFolder.isPending}
            onClick={() =>
              deleting &&
              deleteFolder.mutate(deleting.id, {
                onSuccess: () => {
                  if (selected === deleting.id) onSelect(null);
                  setDeleting(null);
                },
              })
            }
          >
            Delete folder
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function FolderBranch({
  node,
  depth,
  selected,
  onSelect,
  readOnly,
  onAddChild,
  onRename,
  onDelete,
}: {
  node: FolderNode;
  depth: number;
  selected: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  readOnly: boolean;
  onAddChild: (parentId: string) => void;
  onRename: (folder: MediaFolder) => void;
  onDelete: (folder: MediaFolder) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children.length > 0;
  const active = selected === node.id;

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-lg pr-1 transition-colors',
          active ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700/60',
        )}
        style={{ paddingLeft: depth * 12 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-white"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRightIcon className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')} />
          </button>
        ) : (
          <span className="w-[18px]" />
        )}

        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left text-sm',
            active
              ? 'font-medium text-primary-700 dark:text-primary-300'
              : 'text-gray-700 dark:text-gray-300',
          )}
        >
          {active ? (
            <FolderOpenIcon className="h-4 w-4 shrink-0" />
          ) : (
            <FolderIcon className="h-4 w-4 shrink-0 text-gray-400" />
          )}
          <span className="truncate">{node.name}</span>
          <span className="ml-auto shrink-0 text-xs text-gray-400">{node.asset_count}</span>
        </button>

        {!readOnly && (
          <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <IconAction label="Add subfolder" onClick={() => onAddChild(node.id)}>
              <PlusIcon className="h-3.5 w-3.5" />
            </IconAction>
            <IconAction label="Rename" onClick={() => onRename(node)}>
              <PencilIcon className="h-3.5 w-3.5" />
            </IconAction>
            <IconAction label="Delete" onClick={() => onDelete(node)}>
              <TrashIcon className="h-3.5 w-3.5" />
            </IconAction>
          </div>
        )}
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <FolderBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              readOnly={readOnly}
              onAddChild={onAddChild}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderRow({
  icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
        active
          ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/60',
      )}
    >
      <span className={active ? '' : 'text-gray-400'}>{icon}</span>
      <span className="truncate">{label}</span>
      {typeof count === 'number' && <span className="ml-auto text-xs text-gray-400">{count}</span>}
    </button>
  );
}

function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-800 dark:hover:bg-gray-600 dark:hover:text-white"
    >
      {children}
    </button>
  );
}
